import React, { useContext, useRef, useState } from 'react';
import classNames from './addUserForm.module.scss';
import Select from 'antd/lib/select';
import message from 'antd/lib/message';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { FirebaseContext } from '../../context/FirebaseContext';
import { useQuery } from 'react-query';
import LoadingAnimation from '../LoadingAnimation';
import { ArrowRightOutlined } from '@ant-design/icons';
import UploadMultipleUserCsv from '../UploadMultipleUserCsv';
import Button from '../../NecttosComp/Button/Button';
import Input2 from '../../NecttosComp/Input/Input2';

const { Option } = Select;

const userTypess = [
	{ name: 'Student', key: 'student' },
	{ name: 'Teacher', key: 'teacher' },
	{ name: 'Staff', key: 'staff' },
	{ name: 'Scholars', key: 'scholars' },
];

const getDepartmentList = async ({ queryKey }) => {
	const [_, { collegeId }] = queryKey;
	const instance = await getAxiosTokenInstance();
	let resp;
	if (collegeId)
		try {
			resp = await instance.get('/college/classes/getComponent', {
				params: { type: 'departments', collegeId },
			});

			if (resp.data.statusCode === 400) {
				throw new Error(resp.data.message || 'API Error');
			}
		} catch (error) {
			throw new Error(error?.response?.data?.message || error.message || 'API Error');
		}
	return resp?.data;
};

const getClassesList = async ({ queryKey }) => {
	// eslint-disable-next-line
	const [_, { collegeId }] = queryKey;
	const instance = await getAxiosTokenInstance();
	let resp;
	if (collegeId)
		try {
			resp = await instance.get('/college/classes/getClass', {
				params: { collegeId, needAll: true },
			});

			if (resp.data.statusCode === 400) {
				throw new Error(resp.data.message || 'API Error');
			}
		} catch (error) {
			throw new Error(error?.response?.data?.message || error.message || 'API Error');
		}
	return resp?.data;
};

function AddUserForm({ toggleRefresh, userType, setUserType, classId, setClassId, departmentId, setDepartmentId, name, phoneNumber, admisionNo, setAdmisionNo, setPhoneNumber, setName, mode, setMode, dateOfAdmission, setDateOfAdmission, id, setId }) {
	const ref = useRef();

	const [isLoading, setIsLoading] = useState(false);
	const [uploadMultipleUser, setMultipleUserUpload] = useState(false);
	const { collegeId } = useContext(FirebaseContext);

	// State for form values (replacing Formik)
	const [formValues, setFormValues] = useState({
		collegeId,
		type: userType,
		fullName: name,
		phoneNumber: phoneNumber,
		mode: mode || '',
		classId: classId || '',
		departmentId: departmentId || '',
		admisionNo: admisionNo,
		dateOfAdmission: dateOfAdmission,
	});

	// State for form errors (replacing Formik ErrorMessage)
	const [formErrors, setFormErrors] = useState({});

	// Update form values when props change
	React.useEffect(() => {
		setFormValues({
			collegeId,
			type: userType,
			fullName: name,
			phoneNumber: phoneNumber,
			mode: mode || '',
			classId: classId || '',
			departmentId: departmentId || '',
			admisionNo: admisionNo,
			dateOfAdmission: dateOfAdmission,
		});
	}, [collegeId, userType, name, phoneNumber, mode, classId, departmentId, admisionNo, dateOfAdmission]);

	const onAddUser = async (data, actions) => {
		try {
			if (data.fullName) {
				data.type = userType;
				data.classId = formValues?.classId;
				data.mode = mode;
				data.departmentId = formValues?.departmentId;
				setIsLoading(true);
				const instance = await getAxiosTokenInstance();
				let resp;

				if (id) {
					data.id = id;
					data.phoneNumber = data?.phoneNumber?.replace('+91', '');
					resp = await instance.put('/college/users/updateUser', { ...data });
				} else {
					resp = await instance.post('/college/users/addUser', { ...data });
				}
				if (resp.data.statusCode !== 200) {
					throw new Error(resp.data.message || 'API Error');
				}
				toggleRefresh();
				message.success(resp.data?.message || 'User Added');
				actions.resetForm();
			}
		} catch (error) {
			message.error(error?.response?.data?.message || error?.message || 'API Error');
		} finally {
			setIsLoading(false);
		}
	};

	// Handle form input changes
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormValues((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// Handle form submission
	const handleSubmit = (e) => {
		e.preventDefault();

		// Create actions object similar to formikActions
		const actions = {
			resetForm: () => {
				setFormValues({
					collegeId,
					type: userType,
					fullName: '',
					phoneNumber: '',
					mode: '',
					classId: '',
					departmentId: '',
					admisionNo: '',
					dateOfAdmission: '',
				});
			},
		};

		onAddUser(formValues, actions);
		setClassId('');
		setDepartmentId('');
		setName('');
		setPhoneNumber('');
		setAdmisionNo('');
		setDateOfAdmission('');
		setId('');
		setMode('');
	};

	// Reset form function
	const resetForm = () => {
		setFormValues({
			collegeId,
			type: userType,
			fullName: '',
			phoneNumber: '',
			mode: '',
			classId: '',
			departmentId: '',
			admisionNo: '',
			dateOfAdmission: '',
		});

		// Clear form fields if the ref exists
		if (ref.current) {
			ref.current.reset();
		}
	};

	return (
		<div className={classNames.addUserForm}>
			<form ref={ref} className={classNames.userForm} onSubmit={handleSubmit}>
				<Input2 type='drop' options={userTypess || []} optionDisplay='name' didntShowKey optionKey='key' fieldName='Select User' state={userType} setState={setUserType} className='w-full' />
				<div className={classNames.error}>{formErrors.userType}</div>
				{!uploadMultipleUser && (
					<>
						<SelectMode classId={classId} setClassId={setClassId} setDepartmentId={setDepartmentId} departmentId={departmentId} userType={userType} collegeId={collegeId} mode={mode} setMode={setMode} />
						<Input2 type='text' returnKey='fullName' fieldName='Full Name*' state={formValues} setState={(newState) => setFormValues(newState)} className='w-full' required />
					</>
				)}
				{uploadMultipleUser && userType && (
					<UploadMultipleUserCsv
						onCancel={() => {
							resetForm();
							setMultipleUserUpload(false);
						}}
						userType={userType}
					/>
				)}
				{!uploadMultipleUser && (
					<>
						<Input2 type='text' className='w-full' returnKey='phoneNumber' fieldName='Official Mobile Number*' state={formValues} setState={(newState) => setFormValues(newState)} max={10} required />
						<SelectClassOrDep classId={classId} setClassId={setClassId} setDepartmentId={setDepartmentId} departmentId={departmentId} userType={userType} collegeId={collegeId} mode={mode} setMode={setMode} handleChange={handleChange} formValues={formValues} setFormValues={setFormValues} />
					</>
				)}
				{!uploadMultipleUser && (
					<div className='flex justify-between w-full'>
						<Button
							type='refresh'
							onClick={() => {
								setUserType('');
								setClassId('');
								setDepartmentId('');
								setName('');
								setPhoneNumber('');
								setAdmisionNo('');
								setDateOfAdmission('');
								setId('');
								setMode('');
								resetForm();
							}}>
							Clear
						</Button>
						<Button type='submit'>
							Submit
							<ArrowRightOutlined style={{ fontSize: 20, margin: 'auto 0 auto 5px' }} />
						</Button>
					</div>
				)}
			</form>
			<Button
				type='search'
				onClick={() => {
					setMultipleUserUpload(!uploadMultipleUser);
				}}>
				{uploadMultipleUser ? 'Add Single User' : 'Add multiple users'}
			</Button>
			{isLoading && (
				<div className={classNames.loading}>
					<LoadingAnimation />
				</div>
			)}
		</div>
	);
}


function SelectClassOrDep({ userType, collegeId, classId, departmentId, setClassId, setDepartmentId, mode, setMode, handleChange, formValues, setFormValues }) {
	const departmentQuery = useQuery(['departments', { collegeId }], getDepartmentList);
	const classQuery = useQuery(['classes', { collegeId }], getClassesList);

	switch (userType) {
		case 'student':
			return (
				<>
					<Input2 type='text' className='w-full' returnKey='admisionNo' fieldName='Admission Number' state={formValues} setState={setFormValues} placeholder='Ignore: The admission number will be generated automatically' />
					<Input2 type='drop' fieldName='Select Class' className='w-full' state={formValues} setState={setFormValues} options={classQuery?.data?.list || []} returnKey='classId' optionDisplay='className' optionKey='_id' didntShowKey />
				</>
			);
		case 'teacher':
			return (
        <>
          <Input2 type='drop' className='w-full' fieldName='Select Department' state={formValues} setState={setFormValues} options={departmentQuery?.data?.list||[]} optionKey='_id' optionDisplay='name' didntShowKey returnKey='departmentId'/>
        </>
			);
		case 'scholars':
			return (
				<>
					<Input2 type='text' className='w-full' returnKey='admisionNo' fieldName='Admission Number' state={formValues} setState={setFormValues} placeholder='' />
					<Input2 type='date' keyId='dateOfAdmission' returnKey='dateOfAdmission' state={formValues} setState={setFormValues} fieldName='Date of Admission*' required={true} className='w-full' />{' '}
          <Input2 type='drop' className='w-full' fieldName='Select Department' state={formValues} setState={setFormValues} options={departmentQuery?.data?.list||[]} optionKey='_id' optionDisplay='name' didntShowKey returnKey='departmentId'/>
				</>
			);
		default:
			return <></>;
	}
}

function SelectMode({ userType, collegeId, classId, departmentId, setClassId, setDepartmentId, mode, setMode }) {
	switch (userType) {
		case 'scholars':
			return (
				<>
					<Input2
						type='drop'
						options={[
							{ label: 'Part Time', value: 'partTime' },
							{ label: 'Full Time', value: 'fullTime' },
						]}
						optionDisplay='label'
						state={mode}
						setState={setMode}
						optionKey='value'
						didntShowKey
						fieldName='Select Mode'
						className='w-full'
					/>
				</>
			);
		default:
			return <></>;
	}
}

export default AddUserForm;
